import React from "react";
import { Routes, Route } from "react-router-dom";
import Login from 'pages/Login/Login';
import Homepage from "pages/Homepage/Homepage";
import RegisterDevice from "pages/RegisterDevice/RegisterDevice";
import CoinFlip from "features/coin-flip/CoinFlip";
import { usePrivacy } from "providers/PrivacyProvider";
import { useAuth } from "providers/AuthProvider";
import { useR2 } from "providers/R2Provider";

const NotFound = React.lazy(() => import('pages/NotFound/NotFound'));
const Dashboard = React.lazy(() => import('pages/Dashboard/Dashboard'));
const SearchResults = React.lazy(() => import('pages/SearchResults/SearchResults'));
const Users = React.lazy(() => import('features/users/pages/Users/Users'));

const Albums = React.lazy(() => import('features/albums/pages/Albums/Albums'));
const CreateAlbum = React.lazy(() => import('features/albums/pages/CreateAlbum/CreateAlbum'));
const EditAlbum = React.lazy(() => import('features/albums/pages/EditAlbum/EditAlbum'));
const ViewAlbum = React.lazy(() => import('features/albums/pages/ViewAlbum/ViewAlbum'));
import { AlbumFiltersProvider } from 'features/albums/context/albumFiltersContext';

const Days = React.lazy(() => import('features/days/pages/Days/Days'));
const CreateDay = React.lazy(() => import('features/days/pages/CreateDay/CreateDay'));
const EditDay = React.lazy(() => import('features/days/pages/EditDay/EditDay'));
const ViewDay = React.lazy(() => import('features/days/pages/ViewDay/ViewDay'));
import { CreateDayProvider } from "features/days/context/createDayContext";
import { EditDayProvider } from "features/days/context/editDayContext";

const Notes = React.lazy(() => import('features/notes/pages/Notes/Notes'));
const CreateNote = React.lazy(() => import('features/notes/pages/CreateNote/CreateNote'));
const EditNote = React.lazy(() => import('features/notes/pages/EditNote/EditNote'));
const ViewNote = React.lazy(() => import('features/notes/pages/ViewNote/ViewNote'));

const Recipes = React.lazy(() => import('features/recipes/pages/Recipes/Recipes'));
const CreateRecipe = React.lazy(() => import('features/recipes/pages/CreateRecipe/CreateRecipe'));
const EditRecipe = React.lazy(() => import('features/recipes/pages/EditRecipe/EditRecipe'));
const ViewRecipe = React.lazy(() => import('features/recipes/pages/ViewRecipe/ViewRecipe'));
import { RecipeFiltersProvider } from 'features/recipes/context/recipeFiltersContext';

const Phrases = React.lazy(() => import('features/phrases/pages/Phrases/Phrases'));
const CreatePhrase = React.lazy(() => import('features/phrases/pages/CreatePhrase/CreatePhrase'));
const EditPhrase = React.lazy(() => import('features/phrases/pages/EditPhrase/EditPhrase'));
const ViewPhrase = React.lazy(() => import('features/phrases/pages/ViewPhrase/ViewPhrase'));

const Maxims = React.lazy(() => import('features/maxims/pages/Maxims/Maxims'));
const CreateMaxim = React.lazy(() => import('features/maxims/pages/CreateMaxim/CreateMaxim'));
const EditMaxim = React.lazy(() => import('features/maxims/pages/EditMaxim/EditMaxim'));
const ViewMaxim = React.lazy(() => import('features/maxims/pages/ViewMaxim/ViewMaxim'));

const FlashCards = React.lazy(() => import('features/flash-cards/pages/FlashCards/FlashCards'));
const CreateFlashCard = React.lazy(() => import('features/flash-cards/pages/CreateFlashCard/CreateFlashCard'));
const EditFlashCard = React.lazy(() => import('features/flash-cards/pages/EditFlashCard/EditFlashCard'));
const ViewFlashCard = React.lazy(() => import('features/flash-cards/pages/ViewFlashCard/ViewFlashCard'));
const FlashCardsGame = React.lazy(() => import('features/flash-cards/pages/FlashCardsGame/FlashCardsGame'));
import { FlashCardGameFiltersProvider } from 'features/flash-cards/context/flashCardGameFiltersContext';

const ViewPage = React.lazy(() => import('features/pages/pages/ViewPage/ViewPage'));
const CreatePage = React.lazy(() => import('features/pages/pages/CreatePage/CreatePage'));
const EditPage = React.lazy(() => import('features/pages/pages/EditPage/EditPage'));

const Tasks = React.lazy(() => import('features/tasks/pages/Tasks/Tasks'));
const CreateTask = React.lazy(() => import('features/tasks/pages/CreateTask/CreateTask'));
const EditTask = React.lazy(() => import('features/tasks/pages/EditTask/EditTask'));
const ViewTask = React.lazy(() => import('features/tasks/pages/ViewTask/ViewTask'));

const Timeline = React.lazy(() => import('features/timeline/pages/Timeline/Timeline'));
import { TimelineProvider } from "features/timeline/context/timelineContext";

const Wallpapers = React.lazy(() => import('features/wallpapers/pages/Wallpapers/Wallpapers'));
const CreateWallpaper = React.lazy(() => import('features/wallpapers/pages/CreateWallpaper/CreateWallpaper'));
const EditWallpaper = React.lazy(() => import('features/wallpapers/pages/EditWallpaper/EditWallpaper'));
const ViewWallpaper = React.lazy(() => import('features/wallpapers/pages/ViewWallpaper/ViewWallpaper'));

const R2Tasks = React.lazy(() => import('features/r2/features/tasks/pages/Tasks/Tasks'));
const ViewR2Task = React.lazy(() => import('features/r2/features/tasks/pages/ViewTask/ViewTask'));
const CreateR2Task = React.lazy(() => import('features/r2/features/tasks/pages/CreateTask/CreateTask'));
const EditR2Task = React.lazy(() => import('features/r2/features/tasks/pages/EditTask/EditTask'));

const RangerTasks = React.lazy(() => import('features/auto/ranger/features/tasks/pages/Tasks/Tasks'));
const ViewRangerTask = React.lazy(() => import('features/auto/ranger/features/tasks/pages/ViewTask/ViewTask'));
const CreateRangerTask = React.lazy(() => import('features/auto/ranger/features/tasks/pages/CreateTask/CreateTask'));
const EditRangerTask = React.lazy(() => import('features/auto/ranger/features/tasks/pages/EditTask/EditTask'));

const EscapeTasks = React.lazy(() => import('features/auto/escape/features/tasks/pages/Tasks/Tasks'));
const ViewEscapeTask = React.lazy(() => import('features/auto/escape/features/tasks/pages/ViewTask/ViewTask'));
const CreateEscapeTask = React.lazy(() => import('features/auto/escape/features/tasks/pages/CreateTask/CreateTask'));
const EditEscapeTask = React.lazy(() => import('features/auto/escape/features/tasks/pages/EditTask/EditTask'));

const AppRoutes = () => {
  const { token } = useAuth();
  const { privateMode } = usePrivacy();
  const { userCreatedPages } = useR2();

  const baseRoutes = [
    { path: "/", element: <Homepage /> },
    { path: "/auth", element: <Login /> },
    { path: "/register-device", element: <RegisterDevice /> },
    { path: "/auto/escape/tasks", element: <EscapeTasks /> },
    { path: "/auto/escape/tasks/:id", element: <ViewEscapeTask /> },
    { path: "/auto/escape/tasks/create", element: <CreateEscapeTask /> },
    { path: "/auto/escape/tasks/edit/:id", element: <EditEscapeTask /> },
    { path: "/auto/ranger/tasks", element: <RangerTasks /> },
    { path: "/auto/ranger/tasks/:id", element: <ViewRangerTask /> },
    { path: "/auto/ranger/tasks/create", element: <CreateRangerTask /> },
    { path: "/auto/ranger/tasks/edit/:id", element: <EditRangerTask /> },
    { path: "/coin-flip", element: <CoinFlip /> },
    { path: "/search/:query", element: <SearchResults /> },
    { path: "/tag/:query", element: <SearchResults searchTagsOnly /> },
    { path: "/flash-cards", element: <FlashCards /> },
    { path: "/flash-cards-game", element: <FlashCardGameFiltersProvider><FlashCardsGame /></FlashCardGameFiltersProvider> },
    { path: "/flash-cards/:id", element: <ViewFlashCard /> },
    { path: "/flash-cards/create", element: <CreateFlashCard /> },
    { path: "/flash-cards/edit/:id", element: <EditFlashCard /> },
    { path: "/notes", element: <Notes /> },
    { path: "/notes/:id", element: <ViewNote /> },
    { path: "/notes/create", element: <CreateNote /> },
    { path: "/notes/edit/:id", element: <EditNote /> },
    { path: "/food/recipes", element: <RecipeFiltersProvider><Recipes /></RecipeFiltersProvider> },
    { path: "/food/recipes/:id", element: <ViewRecipe /> },
    { path: "/food/recipes/create", element: <CreateRecipe /> },
    { path: "/food/recipes/edit/:id", element: <EditRecipe /> },
    { path: "/music/albums", element: <AlbumFiltersProvider><Albums /></AlbumFiltersProvider> },
    { path: "/music/albums/:id", element: <ViewAlbum /> },
    { path: "/music/albums/create", element: <CreateAlbum /> },
    { path: "/music/albums/edit/:id", element: <EditAlbum /> },
    { path: "/tasks", element: <Tasks /> },
    { path: "/tasks/:id", element: <ViewTask /> },
    { path: "/tasks/create", element: <CreateTask /> },
    { path: "/tasks/edit/:id", element: <EditTask /> },
    { path: "/wallpapers", element: <Wallpapers /> },
    { path: "/wallpapers/preview/:id", element: <ViewWallpaper /> },
    { path: "/wallpapers/create", element: <CreateWallpaper /> },
    { path: "/wallpapers/edit/:id", element: <EditWallpaper /> },
    { path: "/users", element: <Users /> },
  ];

  const privateOnlyRoutes = [
    { path: "/dashboard", element: <Dashboard /> },
    { path: "/days", element: <Days /> },
    { path: "/days/:date", element: <ViewDay /> },
    { path: "/days/create", element: <CreateDayProvider><EditDayProvider><CreateDay /></EditDayProvider></CreateDayProvider> },
    { path: "/days/create/:date", element: <CreateDayProvider><EditDayProvider><CreateDay /></EditDayProvider></CreateDayProvider> },
    { path: "/days/edit/:date", element: <CreateDayProvider><EditDayProvider><EditDay /></EditDayProvider></CreateDayProvider> },
    { path: "/phrases", element: <Phrases /> },
    { path: "/phrases/:id", element: <ViewPhrase /> },
    { path: "/phrases/create", element: <CreatePhrase /> },
    { path: "/phrases/edit/:id", element: <EditPhrase /> },
    { path: "/maxims", element: <Maxims /> },
    { path: "/maxims/:id", element: <ViewMaxim /> },
    { path: "/maxims/create", element: <CreateMaxim /> },
    { path: "/maxims/edit/:id", element: <EditMaxim /> },
    { path: "/r2/tasks", element: <R2Tasks /> },
    { path: "/r2/tasks/:id", element: <ViewR2Task /> },
    { path: "/r2/tasks/create", element: <CreateR2Task /> },
    { path: "/r2/tasks/edit/:id", element: <EditR2Task /> },
    { path: "/life/timeline", element: <TimelineProvider><Timeline /></TimelineProvider> },
    { path: "/pages/create", element: <CreatePage /> },
  ];

  let routes;

  if (!token) {
    routes = (
      <Routes>
        <Route path="*" element={<Login />} />
      </Routes>
    );
  } else {
    let activeRoutes = [...baseRoutes];
    if (privateMode) activeRoutes = [...activeRoutes, ...privateOnlyRoutes];
    
    routes = (
      <Routes>
        {activeRoutes.map(({ path, element }) => (
          <Route key={path} path={path} element={element} />
        ))}

        {userCreatedPages.map(page => (
          <Route key={page.id} path={`${page.pageParent.toLowerCase()}/:id`} element={<ViewPage />} />
        ))}
        {userCreatedPages.map(page => (
          <Route key={`${page.id}-edit`} path={`${page.pageParent.toLowerCase()}/edit/:id`} element={<EditPage />} />
        ))}

        <Route path="*" element={<NotFound />} />
      </Routes>
    );
  }
  
  return (
    routes
  )
};

export default AppRoutes;